/**
 * Ecomhard — Shopify Forms PT-BR
 * - Traduz mensagens internas do Shopify Forms que podem vir em inglês.
 * - Traduz ações de upload de arquivo.
 * - Aplica máscara de CNPJ: 00.000.000/0000-00.
 * - Aplica máscara de CPF: 000.000.000-00.
 *
 * Compatível com Shopify Forms renderizado dentro de Shadow DOM aberto.
 */
(() => {
  'use strict';

  if (window.__ECOMHARD_SHOPIFY_FORMS_PTBR__) return;
  window.__ECOMHARD_SHOPIFY_FORMS_PTBR__ = true;

  const EXACT_TRANSLATIONS = new Map([
    ['Field is required', 'Campo obrigatório'],
    ['Field is required.', 'Campo obrigatório'],
    ['First name is required', 'Nome completo é obrigatório'],
    ['First name is required.', 'Nome completo é obrigatório'],
    ['Last name is required', 'Sobrenome é obrigatório'],
    ['Last name is required.', 'Sobrenome é obrigatório'],
    ['Email is required', 'E-mail é obrigatório'],
    ['Email is required.', 'E-mail é obrigatório'],
    ['Phone number is required', 'Telefone é obrigatório'],
    ['Phone number is required.', 'Telefone é obrigatório'],
    ['Add file', 'Adicionar arquivo'],
    ['Remove file', 'Remover arquivo'],
    ['Replace file', 'Substituir arquivo'],
    ['Choose file', 'Selecionar arquivo'],
    ['Upload file', 'Enviar arquivo'],
    ['Invalid email', 'E-mail inválido'],
    ['Invalid email.', 'E-mail inválido'],
    ['Enter a valid email', 'Informe um e-mail válido'],
    ['Enter a valid email.', 'Informe um e-mail válido'],
    ['Phone number is invalid', 'Telefone inválido'],
    ['Phone number is invalid.', 'Telefone inválido'],
    ['Invalid phone number', 'Telefone inválido'],
    ['Invalid phone number.', 'Telefone inválido'],
    ['File is required', 'Arquivo obrigatório'],
    ['File is required.', 'Arquivo obrigatório'],
    ['File is too large', 'O arquivo é muito grande'],
    ['File is too large.', 'O arquivo é muito grande'],
    ['Invalid file type', 'Tipo de arquivo não permitido'],
    ['Invalid file type.', 'Tipo de arquivo não permitido'],
    ['Something went wrong', 'Ocorreu um erro. Tente novamente'],
    ['Something went wrong.', 'Ocorreu um erro. Tente novamente'],
  ]);

  const DYNAMIC_TRANSLATIONS = [
    {
      pattern: /^Minimum allowed length is 18 characters\.?$/i,
      replacement: 'Informe o CNPJ no formato 00.000.000/0000-00',
    },
    {
      pattern: /^Minimum allowed length is 14 characters\.?$/i,
      replacement: 'Informe o CPF no formato 000.000.000-00',
    },
    {
      pattern: /^Minimum allowed length is (\d+) characters\.?$/i,
      replacement: (_match, count) => `O campo deve ter no mínimo ${count} caracteres`,
    },
    {
      pattern: /^Maximum allowed length is (\d+) characters\.?$/i,
      replacement: (_match, count) => `O campo deve ter no máximo ${count} caracteres`,
    },
    {
      pattern: /^Minimum allowed value is (.+)\.?$/i,
      replacement: (_match, value) => `O valor mínimo permitido é ${value}`,
    },
    {
      pattern: /^Maximum allowed value is (.+)\.?$/i,
      replacement: (_match, value) => `O valor máximo permitido é ${value}`,
    },
  ];

  const observedRoots = new WeakSet();
  const processedCnpjInputs = new WeakSet();
  const processedCpfInputs = new WeakSet();

  const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  function translateTextValue(value) {
    const normalized = normalizeText(value);
    if (!normalized) return null;

    if (EXACT_TRANSLATIONS.has(normalized)) {
      return EXACT_TRANSLATIONS.get(normalized);
    }

    for (const entry of DYNAMIC_TRANSLATIONS) {
      const match = normalized.match(entry.pattern);
      if (!match) continue;

      return typeof entry.replacement === 'function'
        ? entry.replacement(...match)
        : entry.replacement;
    }

    return null;
  }

  function translateTextNode(node) {
    if (!node || node.nodeType !== Node.TEXT_NODE) return;

    const translated = translateTextValue(node.nodeValue);
    if (!translated) return;

    // Preserva apenas o texto útil. Mensagens/botões do Forms não dependem
    // de espaços laterais no text node.
    node.nodeValue = translated;
  }

  function translateElementAttributes(element) {
    if (!(element instanceof Element)) return;

    ['aria-label', 'title'].forEach((attribute) => {
      const current = element.getAttribute(attribute);
      const translated = translateTextValue(current);
      if (translated) element.setAttribute(attribute, translated);
    });

    // Inputs do tipo botão podem carregar o texto em value.
    if (
      element instanceof HTMLInputElement &&
      ['button', 'submit', 'reset'].includes(element.type)
    ) {
      const translated = translateTextValue(element.value);
      if (translated) element.value = translated;
    }
  }

  function formatCnpj(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 14);

    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }

  function formatCpf(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 11);

    return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
  }

  function isCnpjInput(input, root) {
    if (!(input instanceof HTMLInputElement)) return false;

    const directSignals = [
      input.name,
      input.id,
      input.placeholder,
      input.getAttribute('aria-label'),
      input.getAttribute('autocomplete'),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (directSignals.includes('cnpj')) return true;

    // Tenta relacionar o input ao label via for/id.
    if (input.id && root?.querySelector) {
      try {
        const escapedId = window.CSS?.escape ? CSS.escape(input.id) : input.id;
        const label = root.querySelector(`label[for="${escapedId}"]`);
        if (label && /\bcnpj\b/i.test(label.textContent || '')) return true;
      } catch (_error) {
        // Ignora ids que não possam ser usados como seletor.
      }
    }

    // Fallback conservador: inspeciona apenas agrupadores pequenos e próximos.
    // Evita identificar outros campos do formulário como CNPJ só porque o texto
    // "CNPJ" existe em algum ancestral grande do componente.
    let current = input.parentElement;
    for (let depth = 0; current && depth < 2; depth += 1, current = current.parentElement) {
      const text = normalizeText(current.textContent);
      if (text.length <= 80 && /\bcnpj\b/i.test(text)) return true;
    }

    return false;
  }

  function isCpfInput(input, root) {
    if (!(input instanceof HTMLInputElement)) return false;

    const directSignals = [
      input.name,
      input.id,
      input.placeholder,
      input.getAttribute('aria-label'),
      input.getAttribute('autocomplete'),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    // Evita tratar CNPJ ou um campo combinado CPF/CNPJ como CPF.
    if (directSignals.includes('cnpj') || directSignals.includes('cpf/cnpj')) return false;
    if (directSignals.includes('cpf')) return true;

    if (input.id && root?.querySelector) {
      try {
        const escapedId = window.CSS?.escape ? CSS.escape(input.id) : input.id;
        const label = root.querySelector(`label[for="${escapedId}"]`);
        if (label) {
          const labelText = label.textContent || '';
          if (/\bcnpj\b/i.test(labelText) || /cpf\s*\/\s*cnpj/i.test(labelText)) return false;
          if (/\bcpf\b/i.test(labelText)) return true;
        }
      } catch (_error) {
        // Ignora ids que não possam ser usados como seletor.
      }
    }

    let current = input.parentElement;
    for (let depth = 0; current && depth < 2; depth += 1, current = current.parentElement) {
      const text = normalizeText(current.textContent);
      if (text.length <= 80) {
        if (/\bcnpj\b/i.test(text) || /cpf\s*\/\s*cnpj/i.test(text)) return false;
        if (/\bcpf\b/i.test(text)) return true;
      }
    }

    return false;
  }

  function setReactFriendlyValue(input, value) {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    if (descriptor?.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }
  }

  function applyCnpjMask(input) {
    if (processedCnpjInputs.has(input)) return;
    processedCnpjInputs.add(input);

    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('maxlength', '18');

    const mask = () => {
      const formatted = formatCnpj(input.value);
      if (formatted === input.value) return;

      setReactFriendlyValue(input, formatted);
    };

    input.addEventListener('input', mask, true);
    input.addEventListener('change', mask, true);
    input.addEventListener('paste', () => requestAnimationFrame(mask), true);

    // Aplica também caso o navegador/app restaure um valor existente.
    mask();
  }

  function applyCpfMask(input) {
    if (processedCpfInputs.has(input)) return;
    processedCpfInputs.add(input);

    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('maxlength', '14');

    const mask = () => {
      const formatted = formatCpf(input.value);
      if (formatted === input.value) return;

      setReactFriendlyValue(input, formatted);
    };

    input.addEventListener('input', mask, true);
    input.addEventListener('change', mask, true);
    input.addEventListener('paste', () => requestAnimationFrame(mask), true);

    mask();
  }

  function processRoot(root) {
    if (!root) return;

    // Traduz nós já existentes.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode;
    while ((textNode = walker.nextNode())) {
      translateTextNode(textNode);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll('*').forEach((element) => {
        translateElementAttributes(element);

        if (element instanceof HTMLInputElement && isCnpjInput(element, root)) {
          applyCnpjMask(element);
        } else if (element instanceof HTMLInputElement && isCpfInput(element, root)) {
          applyCpfMask(element);
        }

        if (element.shadowRoot) {
          watchRoot(element.shadowRoot);
        }
      });
    }
  }

  function watchRoot(root) {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);

    processRoot(root);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          translateTextNode(mutation.target);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            translateTextNode(node);
            return;
          }

          if (node.nodeType === Node.ELEMENT_NODE) {
            translateElementAttributes(node);
            processRoot(node);
            if (node.shadowRoot) watchRoot(node.shadowRoot);
          }
        });
      });
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function discoverShopifyForms() {
    document.querySelectorAll('shopify-forms-embed').forEach((embed) => {
      if (embed.shadowRoot) watchRoot(embed.shadowRoot);
    });
  }

  // Observa o documento porque o app Shopify Forms pode ser carregado depois
  // do restante do tema.
  watchRoot(document.documentElement);
  discoverShopifyForms();

  const formsDiscoveryObserver = new MutationObserver(discoverShopifyForms);
  formsDiscoveryObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Alguns updates internos do app não propagam mutações para o host.
  // Este fallback é leve e serve apenas para descobrir novos embeds/shadow roots.
  const discoveryInterval = window.setInterval(discoverShopifyForms, 1000);
  window.setTimeout(() => window.clearInterval(discoveryInterval), 30000);
})();
